<template>
  <HelpCenterPost
    v-for="({ post, lazy, showsMore }) in entries"
    :key="post.id"
    shown-in-flow
    :lazy="lazy"
    :post="post"
    :author-map="authorMap"
    :category-map="categoryMap"
  >
    <template #separator>
      <div class="help-center-post-flow__hr" />

      <button
        v-if="showsMore"
        class="help-center-post-flow__button"
        :disabled="loadInProgress"
        @click="loadWithProgress"
      >
        See More
        <HelpCenterIcon
          v-if="loadInProgress"
          name="loader"
          size-units="5"
          class="help-center-post-flow__icon"
        />
      </button>
    </template>
  </HelpCenterPost>

  <HelpCenterError :error="error" />
</template>

<script setup>
import { ref, watch, toRef, computed, onUnmounted } from 'vue'
import { cancelRequest, dedupedRequest, ignoreCancelled } from 'gleam-shared/network/http'
import useInProgress from 'gleam-shared/composables/useInProgress'

import HelpCenterPost from 'help-center/components/HelpCenterPost'
import HelpCenterIcon from 'help-center/components/HelpCenterIcon'
import HelpCenterError from 'help-center/components/HelpCenterError'

const SIGN = 'HelpCenterPostFlow'

const props = defineProps({
  authorMap: {
    type: Object,
    required: true
  },
  categoryMap: {
    type: Object,
    required: true
  },
  paginatedPosts: {
    type: Object,
    required: true
  }
})

const more = ref(null)
const error = ref(null)
const posts = ref(null)
const [loadInProgress, loadWithProgress] = useInProgress(load)

const entries = computed(() => {
  return posts.value.map((post, index) => {
    const lazy = index >= 1
    const showsMore = more.value && index === (posts.value.length - 1)

    return { post, lazy, showsMore }
  })
})

watch(toRef(props, 'paginatedPosts'), (paginatedPosts) => {
  more.value = paginatedPosts.more
  posts.value = paginatedPosts.posts
}, { immediate: true })

onUnmounted(() => {
  cancelRequest(SIGN)
})

function load() {
  return dedupedRequest(more.value, SIGN).then((data) => {
    more.value = data.more
    posts.value = posts.value.concat(data.posts)
  }).catch(ignoreCancelled((err) => {
    error.value = err
  }))
}
</script>

<style lang="scss">
@import 'help-center/mixins';

.help-center-post-flow {
  &__hr {
    @include help-center-hr;
    margin-top: 24px;
  }

  &__button {
    @include help-center-font(14);
    @include help-center-highlight-link;

    gap: 8px;
    display: inline-flex;
    align-items: center;

    width: fit-content;
    margin-top: 24px;

    font-weight: var(--help-center-bold);
    text-transform: uppercase;
  }

  &__icon {
    @include help-center-spin;
  }
}
</style>
